import { Gauge, TrendingUp, Cog, HardDrive, MemoryStick, Cpu } from "lucide-react";
import { Reveal, SectionHeading, waLink } from "./shared";

const PILLARS = [
  { icon: Gauge, title: "Faster Speed", desc: "Boost system performance" },
  { icon: TrendingUp, title: "More Reliability", desc: "High-quality components" },
  { icon: Cog, title: "Longer Life", desc: "Upgrade today, worry less tomorrow" },
];

const SPOTLIGHTS = [
  {
    icon: HardDrive,
    title: "SSD Upgrades",
    spec: "500MB/s HDD → 3500MB/s Gen4 NVMe",
    desc: "Faster boot & load times with a full 1:1 data clone guarantee — everything exactly where you left it.",
    img: "https://images.unsplash.com/photo-1721333091889-0b4fe5b2b28f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxjb21wdXRlciUyMHJlcGFpciUyMGxhcHRvcCUyMG1vdGhlcmJvYXJkJTIwc3NkfGVufDB8fHx8MTc4OTU3Njc2OHww&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: MemoryStick,
    title: "RAM Upgrades",
    spec: "8GB → 16GB / 32GB dual-channel",
    desc: "Smoother multitasking for creative apps, heavy browser tabs and business workloads.",
    img: "https://images.pexels.com/photos/33792050/pexels-photo-33792050.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    icon: Cpu,
    title: "Performance Boost",
    spec: "Thermal repaste · OS tuning",
    desc: "Unlock your PC's potential with a thermal overhaul and deep system optimization.",
    img: "https://images.unsplash.com/photo-1721333089073-215a56fd710c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxjb21wdXRlciUyMHJlcGFpciUyMGxhcHRvcCUyMG1vdGhlcmJvYXJkJTIwc3NkfGVufDB8fHx8MTc4OTU3Njc2OHww&ixlib=rb-4.1.0&q=85",
  },
];

export const Upgrades = () => (
  <section id="upgrades" data-testid="upgrades-section" className="relative py-24 sm:py-32 bg-navy/40 overflow-hidden">
    <div className="absolute top-10 -left-32 w-[26rem] h-[26rem] rounded-full bg-brand-lime/8 blur-[140px] pointer-events-none" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <SectionHeading
        eyebrow="Hardware Upgrades"
        title="Better hardware."
        accent="Better performance."
        sub="A targeted upgrade often beats buying a new machine. AFZ advises the right part, installs it cleanly and clones your data across."
      />

      <div className="mt-14 grid sm:grid-cols-3 gap-5">
        {PILLARS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.08}>
            <div className="flex items-center gap-4 bg-card/80 border border-slate-800 rounded-xl p-5 hover:border-brand-lime/50 transition-all duration-300 group">
              <div className="w-12 h-12 shrink-0 rounded-full border-2 border-brand-lime/60 bg-brand-lime/10 flex items-center justify-center text-brand-lime shadow-[0_0_18px_rgba(170,255,0,0.25)] group-hover:scale-110 transition-transform duration-300">
                <p.icon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-display text-lg font-bold uppercase text-white">{p.title}</h3>
                <p className="text-sm text-slate-400">{p.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-8 grid md:grid-cols-3 gap-6">
        {SPOTLIGHTS.map((s, i) => (
          <Reveal key={s.title} delay={i * 0.1}>
            <div
              data-testid={`upgrade-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="group relative bg-card/80 border border-slate-800 rounded-2xl overflow-hidden hover:border-brand-lime/50 transition-all duration-300 hover:shadow-[0_0_35px_rgba(170,255,0,0.12)] hover:-translate-y-1"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={s.img}
                  alt={s.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/30 to-transparent" />
                <div className="absolute bottom-3 left-4 flex items-center gap-2">
                  <s.icon className="w-4 h-4 text-brand-lime" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-lime">{s.spec}</span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-display text-2xl font-bold uppercase text-white">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-6 border border-brand-lime/30 rounded-2xl p-6 sm:p-8 bg-gradient-to-r from-brand-lime/10 via-transparent to-brand-cyan/10 clip-corner">
          <p className="font-display text-2xl sm:text-3xl font-bold uppercase text-white text-center sm:text-left">
            Not sure what your device needs? <span className="text-brand-lime">Free inspection.</span>
          </p>
          <a
            data-testid="upgrades-quote-button"
            href={waLink("Hi AFZ Tech Solution, I'm interested in a hardware upgrade (SSD/RAM). Can I get a quote?")}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 inline-flex items-center gap-2 bg-brand-lime text-void font-display font-bold uppercase px-7 py-3.5 rounded-full hover:bg-brand-limedark hover:scale-[1.03] transition-all duration-300 shadow-[0_0_24px_rgba(170,255,0,0.35)]"
          >
            Get Upgrade Quote
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);
